import React, { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import api from "@/lib/api";
import { Flag } from "lucide-react";

const REASONS = [
  { value: "spam", label: "Spam" },
  { value: "sara", label: "Mengandung SARA" },
  { value: "bahasa", label: "Bahasa Tidak Sesuai" },
  { value: "misinformasi", label: "Salah Informasi" },
  { value: "other", label: "Lainnya (sebutkan)" },
];

/**
 * ReportDialog — modal reusable untuk pengguna melaporkan thread atau balasan.
 * Props:
 *   open (bool), onOpenChange (fn)
 *   targetType: "thread" | "reply"
 *   targetId:   string (thread_id atau reply_id)
 *   onSuccess?  optional callback after submit
 */
export default function ReportDialog({ open, onOpenChange, targetType, targetId, onSuccess }) {
  const [reason, setReason] = useState("spam");
  const [detail, setDetail] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const reset = () => {
    setReason("spam");
    setDetail("");
    setSubmitting(false);
  };

  const submit = async () => {
    if (submitting || !targetId) return;
    if (reason === "other" && detail.trim().length < 3) {
      toast.error("Mohon jelaskan alasan pelaporan minimal 3 karakter");
      return;
    }
    setSubmitting(true);
    try {
      const res = await api.post("/forum/reports", {
        target_type: targetType,
        target_id: targetId,
        reason,
        detail: detail.trim(),
      });
      if (res.data?.already) {
        toast.info("Anda sudah pernah melaporkan konten ini. Tim moderator sedang meninjau.");
      } else {
        toast.success("Terima kasih. Laporan Anda dikirim ke moderator.");
      }
      reset();
      onOpenChange(false);
      onSuccess && onSuccess();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Gagal mengirim laporan. Coba lagi.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) reset(); onOpenChange(v); }}>
      <DialogContent data-testid="report-dialog" className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 font-serif-display text-xl">
            <Flag size={18} className="text-[#B91C1C]" />
            Laporkan {targetType === "thread" ? "diskusi" : "komentar"}
          </DialogTitle>
        </DialogHeader>

        <p className="text-sm text-[#4B5563]">
          Bantu kami menjaga komunitas tetap sehat. Pilih alasan pelaporan — moderator akan meninjau dalam 24 jam.
        </p>

        <div className="space-y-2 mt-2" role="radiogroup" aria-label="Alasan pelaporan">
          {REASONS.map((r) => (
            <label
              key={r.value}
              data-testid={`report-reason-${r.value}`}
              className={
                "flex items-center gap-3 px-3 py-2.5 rounded-xl border cursor-pointer transition-colors " +
                (reason === r.value
                  ? "bg-[#FEF2F2] border-[#FCA5A5]"
                  : "bg-white border-[#EAE7E0] hover:bg-[#F9F8F6]")
              }
            >
              <input
                type="radio"
                name="report-reason"
                value={r.value}
                checked={reason === r.value}
                onChange={() => setReason(r.value)}
                className="accent-[#B91C1C]"
              />
              <span className="text-sm text-[#1F2937]">{r.label}</span>
            </label>
          ))}
        </div>

        {reason === "other" && (
          <Textarea
            data-testid="report-detail-input"
            value={detail}
            onChange={(e) => setDetail(e.target.value)}
            placeholder="Sebutkan alasan pelaporan..."
            className="mt-2 min-h-[80px] resize-none"
            maxLength={400}
          />
        )}
        {reason !== "other" && (
          <Textarea
            data-testid="report-detail-optional"
            value={detail}
            onChange={(e) => setDetail(e.target.value)}
            placeholder="Detail tambahan (opsional)"
            className="mt-2 min-h-[64px] resize-none"
            maxLength={400}
          />
        )}

        <DialogFooter className="mt-2">
          <Button
            variant="outline"
            onClick={() => { reset(); onOpenChange(false); }}
            data-testid="report-cancel-btn"
            className="rounded-full border-[#EAE7E0]"
          >
            Batal
          </Button>
          <Button
            onClick={submit}
            disabled={submitting}
            data-testid="report-submit-btn"
            className="rounded-full bg-[#B91C1C] hover:bg-[#991B1B] text-white"
          >
            {submitting ? "Mengirim..." : "Kirim laporan"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
